import type { I18n } from '@lingui/core';
import { t } from '@lingui/core/macro';
export interface OpenClawOnboardingOptions {
  serverUrl: string;
  apiKey: string;
}

/**
 * Produces the canonical, additive OpenClaw setup guidance for a Borgee account.
 * Operators must inspect their configuration and merge each addition rather than
 * replacing existing accounts, bindings, tool policy, or allow-lists.
 */
export function buildOpenClawOnboardingPlan(
  i18n: I18n,
  { serverUrl, apiKey }: OpenClawOnboardingOptions,
): string {
  return `${t(i18n)`To accept this invitation, add Borgee alongside your existing OpenClaw channels.`}

# ${t(i18n)`Install the Borgee plugin (skip if already installed)`}
openclaw plugins install @borgee/openclaw-plugin

# ${t(i18n)`Add a Borgee account`}
# ${t(i18n)`Pick a simple account slug (letters, numbers, - or _, for example "borgee").`}
# ${t(i18n)`Preserve any existing Borgee accounts and channel policy.`}
openclaw config set channels.borgee.accounts.YOUR_ACCOUNT_SLUG.baseUrl '"${serverUrl}"'
openclaw config set channels.borgee.accounts.YOUR_ACCOUNT_SLUG.apiKey '"${apiKey}"'

# ${t(i18n)`Enable the plugin and token-usage telemetry (skip if already enabled)`}
# ${t(i18n)`This lets Borgee read final-turn usage for telemetry. Borgee does not persist message content.`}
openclaw config set plugins.entries.borgee.enabled true
openclaw config set plugins.entries.borgee.hooks.allowConversationAccess true

# ${t(i18n)`Ensure Borgee tool access while preserving your existing tool profile or allow-list`}
# ${t(i18n)`Inspect your current tools configuration, then merge message, borgee_task and borgee_list_zones into its additional allow-list.`}
# ${t(i18n)`Example additive configuration:`} {"tools":{"alsoAllow":["message","borgee_task","borgee_list_zones"]}}

# ${t(i18n)`Add a Borgee binding safely`}
# ${t(i18n)`Inspect your existing bindings and merge this entry; do not replace the bindings array.`}
# ${t(i18n)`Replace YOUR_AGENT_ID and YOUR_ACCOUNT_SLUG:`}
# {"agentId":"YOUR_AGENT_ID","match":{"channel":"borgee","accountId":"YOUR_ACCOUNT_SLUG"}}

# ${t(i18n)`Restart to connect`}
openclaw gateway restart`;
}
